import React, { useEffect } from 'react';
import { Modal, Form, Input, Select, Typography, App } from 'antd';
import { Table } from '../../../types/tables';
import { useGetWaitersQuery } from '../../../services/usersApi';
import '../tables.css';
import { useAppSelector } from '../../../hooks/redux';
import { useRestaurantTimeZone } from '../../../hooks/useTimeZone';
import GuestCounters from '../GuestCounters';

const { Text } = Typography;
const { Option } = Select;

interface OpenTableModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: OpenTableData) => Promise<void>;
  table?: Table;
  loading?: boolean;
}

interface OpenTableData {
  customerCount: number;
  adults: number;
  children: number;
  waiterId?: string;
  notes?: string;
}

const OpenTableModal: React.FC<OpenTableModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  table,
  loading = false,
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [adults, setAdults] = React.useState(1); // Default: 1 adult
  const [children, setChildren] = React.useState(0); // Default: 0 children

  // Get current user from Redux
  const { user } = useAppSelector((state) => state.auth);
  const { formatInTimeZone } = useRestaurantTimeZone();

  // Get waiters (Mesero and Cajero/Mesero roles) from API - backend already filters
  const { data: waiters, isLoading: usersLoading } = useGetWaitersQuery();

  // Build the list of available assignees: los meseros del restaurante, más el
  // usuario actual si no es uno de ellos (un admin o gerente puede quedarse la
  // mesa a su nombre).
  const availableAssignees = React.useMemo(() => {
    const waitersList = waiters || [];

    if (user && !waitersList.some(w => w.id === user.id)) {
      return [
        { id: user.id, firstName: user.firstName, lastName: user.lastName },
        ...waitersList
      ];
    }

    return waitersList;
  }, [waiters, user]);

  // Preseleccionar al usuario actual, sea mesero o no. Puede cambiarlo: un
  // mesero abre la mesa y se la deja a un compañero. Quién ABRIÓ la mesa se
  // guarda aparte (Order.UserId / UserName), así que reasignar no borra el
  // rastro de auditoría.
  useEffect(() => {
    if (visible && user && availableAssignees.some(w => w.id === user.id)) {
      form.setFieldsValue({ waiterId: user.id });
    }
  }, [visible, user, availableAssignees, form]);

  const totalCustomers = adults + children;

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const submitData = {
        ...values,
        adults,
        children,
        customerCount: totalCustomers
      };
      await onSubmit(submitData);
      handleReset();
      message.success(exceedsCapacity ? 'Mesa abierta exitosamente (sobrecapacidad)' : 'Mesa abierta exitosamente');
    } catch (error) {
      console.error('Error al abrir mesa:', error);
      message.error('Error al abrir mesa');
    }
  };

  const handleCancel = () => {
    handleReset();
    onCancel();
  };

  const handleReset = () => {
    form.resetFields();
    setAdults(1);
    setChildren(0);
  };

  // A merged primary seats the whole group, not just its own table
  const groupCapacity = table
    ? table.capacity + (table.mergedTables?.reduce((sum, t) => sum + t.capacity, 0) ?? 0)
    : undefined;
  const mergedNumbers = table?.mergedTables?.map(t => t.number).join(', ');
  const capacity = groupCapacity || 20;

  // Validate minimum requirement (at least 1 person)
  const isValidTotal = totalCustomers >= 1;
  const exceedsCapacity = totalCustomers > capacity;

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', paddingRight: 28 }}>
          <span>Abrir mesa {table?.number}</span>
          {/* Contexto como meta en el título: área, capacidad y hora, sin card verde */}
          <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
            {table?.areaName} · capacidad {groupCapacity}
            {mergedNumbers ? ` (con Mesa ${mergedNumbers})` : ''} · {formatInTimeZone(new Date(), 'HH:mm')}
          </Text>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={550}
      wrapClassName="open-table-modal"
      okText={exceedsCapacity ? 'Abrir con sobrecapacidad' : 'Abrir mesa'}
      cancelText="Cancelar"
      style={{ top: 30 }}
      okButtonProps={{
        disabled: !isValidTotal,
        size: 'large'
      }}
      cancelButtonProps={{ size: 'large' }}
    >
      <GuestCounters
        adults={adults}
        children={children}
        onAdultsChange={setAdults}
        onChildrenChange={setChildren}
        capacity={capacity}
        style={{ margin: '8px 0 16px' }}
      />

      <Form form={form} layout="vertical" requiredMark={false}>
        <div className="open-table-fields" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
          <Form.Item
            name="waiterId"
            label="Mesero"
            rules={[{ required: true, message: 'Debe asignar un mesero' }]}
          >
            <Select
              placeholder="Seleccionar mesero"
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="children"
              loading={usersLoading}
              disabled={usersLoading}
            >
              {availableAssignees.map(assignee => (
                <Option key={assignee.id} value={assignee.id}>
                  {assignee.firstName} {assignee.lastName}
                  {assignee.id === user?.id ? ' (Yo)' : ''}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="notes" label="Notas (opcional)">
            <Input.TextArea
              placeholder="Solicitudes especiales…"
              rows={1}
              maxLength={150}
              autoSize={{ minRows: 1, maxRows: 3 }}
            />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

export default OpenTableModal;